import './index.scss';

import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Switch, Route, Redirect, useParams } from 'react-router-dom';

import { INTERNAL_USER_ROLES } from '@/src/lib/authorization/authorization';
import { RootState } from '@/src/reducers/types';

import Account from './components/Account/Account';
import Apps from './components/Apps/Apps';
import { ProfileSection } from './components/Profile/ProfileSection';
import Sidebar from './Elements/Sidebar/Sidebar';
import { useGetProjectPermissions } from '../api/getProjectPermissions';
import SectionLoader from '../../common/Elements/Spinner/SectionLoader/SectionLoader';

const Settings = () => {
  const project = useSelector((store: RootState) => store.project);
  const { projectId } = useParams<{ projectId: string }>();

  const [state, setState] = useState({
    scrollId: 'general',
    activeMenu: { id: 'companyProfile', label: 'Company Profile' },
    menus: [
      {
        id: 'companyProfile',
        label: 'Company Profile',
        path: '/',
        children: [],
      },
      {
        id: 'account',
        label: 'Account',
        path: '/account',
        children: [
          { id: 'general', label: 'General' },
          { id: 'dashboard', label: 'Reports', allowedRoles: INTERNAL_USER_ROLES },
          { id: 'inbox', label: 'Inbox' },
          { id: 'members', label: 'Members' },
          { id: 'workstreams', label: 'Workstreams' },
          { id: 'calendar', label: 'Calendar' },
        ],
      },
      {
        id: 'apps',
        label: 'Apps',
        path: '/apps',
        children: [],
      },
    ],
  });

  const enabledProfile =
    project.Selected &&
    project.Selected.type &&
    project.Selected.type.type === 'Client' &&
    project.Selected.isActive;

  const projectPermissonsQuery = useGetProjectPermissions({
    projectId: +projectId,
    config: { enabled: !!projectId },
  });

  const allowedMenus = useMemo(() => {
    const actions = projectPermissonsQuery.data?.data?.settings?.actions || [];

    return state.menus.map((menu) => ({
      ...menu,
      children: menu.children?.filter((child) => {
        // Only apply permission-based visibility for 'dashboard'
        if (child.id === 'dashboard') {
          const fullActionName = `${menu.id}_${child.id}`;
          return actions.some((a) => a.allowed && a.name === fullActionName);
        }
        // Other children remain always visible
        return true;
      }),
    }));
  }, [projectPermissonsQuery.data?.data?.settings?.actions, state.menus]);

  // For all menus depnendent on project permissions setting specific conditional display
  // const allowedMenus = useMemo(() => {
  //   const actions = projectPermissonsQuery.data?.data?.settings?.actions || [];

  //   // If no actions yet → show all menus except dashboard
  //   if (!actions.length) {
  //     return state.menus.map((menu) => ({
  //       ...menu,
  //       children: menu.children?.filter((child) => child.id !== 'dashboard'),
  //     }));
  //   }

  //   return state.menus
  //     .filter((menu) => {
  //       const topLevelAllowed = actions.some((a) => a.allowed && a.name === menu.id);

  //       const childrenAllowed = menu.children?.some((child) => {
  //         const fullActionName = `${menu.id}_${child.id}`;
  //         return actions.some((a) => a.allowed && a.name === fullActionName);
  //       });

  //       return topLevelAllowed || childrenAllowed;
  //     })
  //     .map((menu) => ({
  //       ...menu,
  //       children: menu.children?.filter((child) => {
  //         const fullActionName = `${menu.id}_${child.id}`;
  //         // dashboard is excluded by default unless explicitly allowed
  //         if (child.id === 'dashboard') {
  //           return actions.some((a) => a.allowed && a.name === fullActionName);
  //         }
  //         return actions.some((a) => a.allowed && a.name === fullActionName);
  //       }),
  //     }));
  // }, [projectPermissonsQuery.data?.data?.settings?.actions, state.menus]);

  const actions = projectPermissonsQuery.data?.data?.settings?.actions || [];
  const accountActions = actions.filter((a) => a.name.startsWith('account_'));
  const allAccountActionsDisallowed =
    accountActions.length > 0 && accountActions.every((a) => a.allowed === false);

  const visibleMenus = allowedMenus.filter((m) => {
    if (m.id === 'account' && allAccountActionsDisallowed) return false;
    return (!enabledProfile && m.id !== 'companyProfile') || enabledProfile;
  });

  const handleUpdateSidebar = (menu) => {
    setState({ ...state, activeMenu: menu, scrollId: menu.id });
  };

  return (
    <div className={clsx('projectSettings', 'accountSettings--wrapper', 'tw-bg-cfo-grey-200')}>
      <div className={clsx('pageContainer', 'accountSettings--container', 'tw-flex', 'tw-w-full')}>
        <Sidebar
          menus={visibleMenus}
          activeMenu={state.activeMenu}
          onUpdateSidebar={handleUpdateSidebar}
          profile={enabledProfile}
        />

        {project.Selected.id && (
          <div className={clsx('settingsBody', 'tw-w-full', 'tw-p-0 tw-mr-0')}>
            <Switch>
              {enabledProfile ? (
                <>
                  <Route path={`/projects/:projectId/settings`} exact component={ProfileSection} />
                  <Route
                    path={`/projects/:projectId/settings/account`}
                    exact
                    render={({ history }) => (
                      <Account scrollId={state.scrollId} history={history} />
                    )}
                  />

                  {/* Apps Settings */}
                  <Route
                    path={`/projects/:projectId/settings/apps`}
                    exact
                    render={() => <Apps />}
                  />
                </>
              ) : (
                <>
                  <Route
                    path={`/projects/:projectId/settings`}
                    exact
                    render={() => <Redirect to={`/projects/${projectId}/settings/account`} />}
                  />
                  <Route
                    path={`/projects/:projectId/settings/account`}
                    exact
                    render={({ history }) => (
                      <Account scrollId={state.scrollId} history={history} />
                    )}
                  />
                  <Route path={`/projects/:projectId/settings/apps`} exact component={Apps} />
                </>
              )}
            </Switch>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
